import React from 'react';
import { Sun, Lightbulb, Zap, Battery, Wind, Gauge, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const EnergySolutions = () => {
  const { language } = useLanguage();

  const products = [
    {
      icon: Sun,
      title: { fr: 'Panneaux Solaires', en: 'Solar Panels' },
      description: { fr: 'Énergie propre et renouvelable', en: 'Clean and renewable energy' },
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276',
      specs: [
        { fr: 'Panneaux monocristallins et polycristallins', en: 'Monocrystalline and polycrystalline panels' },
        { fr: 'Puissance 300W à 500W', en: 'Power 300W to 500W' },
        { fr: 'Garantie 25 ans', en: '25 year warranty' },
        { fr: 'Rendement jusqu\'à 22%', en: 'Efficiency up to 22%' },
        { fr: 'Installation clé en main', en: 'Turnkey installation' }
      ]
    },
    {
      icon: Lightbulb,
      title: { fr: 'Éclairage LED', en: 'LED Lighting' },
      description: { fr: 'Économie d\'énergie et longue durée', en: 'Energy saving and long life' },
      image: 'https://images.unsplash.com/photo-1532007271951-c487760934ae',
      specs: [
        { fr: 'Ampoules LED 5W à 50W', en: 'LED bulbs 5W to 50W' },
        { fr: 'Tubes LED T8 et T5', en: 'T8 and T5 LED tubes' },
        { fr: 'Projecteurs LED extérieurs', en: 'Outdoor LED floodlights' },
        { fr: 'Économie jusqu\'à 80%', en: 'Savings up to 80%' },
        { fr: 'Durée de vie 50,000h', en: '50,000h lifespan' }
      ]
    },
    {
      icon: Zap,
      title: { fr: 'Groupes Électrogènes', en: 'Generators' },
      description: { fr: 'Alimentation de secours fiable', en: 'Reliable backup power' },
      image: 'https://images.unsplash.com/photo-1681263576084-e054b2d89903',
      specs: [
        { fr: 'Puissance 5kVA à 500kVA', en: 'Power 5kVA to 500kVA' },
        { fr: 'Essence et diesel', en: 'Gasoline and diesel' },
        { fr: 'Démarrage automatique', en: 'Automatic start' },
        { fr: 'Insonorisé disponible', en: 'Soundproof available' },
        { fr: 'Maintenance incluse', en: 'Maintenance included' }
      ]
    },
    {
      icon: Battery,
      title: { fr: 'Batteries de Secours', en: 'Backup Batteries' },
      description: { fr: 'Stockage d\'énergie pour continuité', en: 'Energy storage for continuity' },
      image: 'https://images.unsplash.com/photo-1531492746076-161ca9bcad58',
      specs: [
        { fr: 'Batteries lithium-ion', en: 'Lithium-ion batteries' },
        { fr: 'Capacités 5kWh à 100kWh', en: 'Capacities 5kWh to 100kWh' },
        { fr: 'Gestion intelligente BMS', en: 'Intelligent BMS management' },
        { fr: 'Durée de vie 10+ ans', en: '10+ year lifespan' },
        { fr: 'Compatible solaire', en: 'Solar compatible' }
      ]
    },
    {
      icon: Sun,
      title: { fr: 'Systèmes Solaires Complets', en: 'Complete Solar Systems' },
      description: { fr: 'Solution complète d\'autonomie énergétique', en: 'Complete energy autonomy solution' },
      image: 'https://images.unsplash.com/photo-1613665813446-82a78c468a1d',
      specs: [
        { fr: 'Panneaux + Onduleurs + Batteries', en: 'Panels + Inverters + Batteries' },
        { fr: 'Systèmes isolés ou hybrides', en: 'Isolated or hybrid systems' },
        { fr: 'Monitoring à distance', en: 'Remote monitoring' },
        { fr: 'Dimensionnement sur mesure', en: 'Custom sizing' },
        { fr: 'Support technique 24/7', en: '24/7 technical support' }
      ]
    },
    {
      icon: Gauge,
      title: { fr: 'Régulateurs et Onduleurs', en: 'Regulators and Inverters' },
      description: { fr: 'Gestion optimale de l\'énergie', en: 'Optimal energy management' },
      image: 'https://images.unsplash.com/photo-1606904825846-647eb07f5be2',
      specs: [
        { fr: 'Onduleurs 1kVA à 50kVA', en: 'Inverters 1kVA to 50kVA' },
        { fr: 'MPPT et PWM', en: 'MPPT and PWM' },
        { fr: 'Onde sinusoïdale pure', en: 'Pure sine wave' },
        { fr: 'Rendement > 95%', en: 'Efficiency > 95%' },
        { fr: 'Protection surcharge', en: 'Overload protection' }
      ]
    }
  ];

  const benefits = [
    {
      title: { fr: 'Économie d\'Énergie', en: 'Energy Savings' },
      description: { fr: 'Réduisez vos factures jusqu\'à 70%', en: 'Reduce your bills up to 70%' }
    },
    {
      title: { fr: 'Indépendance Énergétique', en: 'Energy Independence' },
      description: { fr: 'Moins de dépendance au réseau', en: 'Less grid dependency' }
    },
    {
      title: { fr: 'Écologique', en: 'Eco-Friendly' },
      description: { fr: 'Réduisez votre empreinte carbone', en: 'Reduce your carbon footprint' }
    },
    {
      title: { fr: 'Fiabilité', en: 'Reliability' },
      description: { fr: 'Alimentation continue garantie', en: 'Continuous power guaranteed' }
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Sun className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Énergie & Éclairage' : 'Energy & Lighting'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Solutions énergétiques durables pour votre entreprise' : 'Sustainable energy solutions for your business'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Solutions Énergétiques' : 'Our Energy Solutions'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={product.image} alt={product.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{product.description[language]}</p>
                    <ul className="space-y-3">
                      {product.specs.map((spec, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{spec[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Avantages de Nos Solutions' : 'Benefits of Our Solutions'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-6 text-center bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200 hover:border-blue-500 transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title[language]}</h3>
                <p className="text-gray-600 text-sm">{benefit.description[language]}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Audit Énergétique Gratuit' : 'Free Energy Audit'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Nos experts analysent vos besoins et vous proposent la meilleure solution' : 'Our experts analyze your needs and offer you the best solution'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander un Audit' : 'Request an Audit'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default EnergySolutions;
